import { useState } from "react";

export interface UserCasingString {
  id: string;
  casing_type: string; // e.g., "Surface", "Intermediate", "Production", "Liner"
  size: string;        // e.g., "9 5/8"
  weight: string;      // e.g., "47"
  hole_size: string;   // e.g., "12 1/4", "8 3/4" — borehole/drill bit diameter in inches
  depth_set: number | null;
  top_of_cement: number | null;
  bottom_of_cement: number | null;
}

interface CasingEntryFormProps {
  casingStrings: UserCasingString[];
  onAdd: (casing: UserCasingString) => void;
  onRemove: (id: string) => void;
  onUpdate: (id: string, field: keyof UserCasingString, value: string | number | null) => void;
}

// Common casing sizes for dropdown
const CASING_SIZES = [
  "4 1/2",
  "5 1/2",
  "7",
  "7 5/8",
  "8 5/8",
  "9 5/8",
  "10 3/4",
  "11 3/4",
  "13 3/8",
  "16",
  "20",
  "Other",
];

// Common hole sizes for each casing size
const HOLE_SIZES: Record<string, string[]> = {
  "4 1/2": ["6 1/4", "6 1/2", "6 3/4"],
  "5 1/2": ["7 7/8", "8 1/2", "8 3/4"],
  "7": ["8 3/4", "9 7/8", "10 5/8"],
  "7 5/8": ["9 7/8", "10 5/8", "11"],
  "8 5/8": ["11", "12 1/4"],
  "9 5/8": ["12 1/4", "14 3/4"],
  "10 3/4": ["14 3/4", "15"],
  "11 3/4": ["14 3/4", "15", "17 1/2"],
  "13 3/8": ["17 1/2"],
  "16": ["20", "22"],
  "20": ["26"],
};

// Common casing weights for reference
const CASING_WEIGHTS: Record<string, string[]> = {
  "4 1/2": ["9.5", "10.5", "11.6", "13.5"],
  "5 1/2": ["14", "15.5", "17", "20", "23"],
  "7": ["17", "20", "23", "26", "29", "32"],
  "7 5/8": ["24", "26.4", "29.7", "33.7", "39"],
  "8 5/8": ["24", "28", "32", "36", "40", "44"],
  "9 5/8": ["32.3", "36", "40", "43.5", "47", "53.5"],
  "10 3/4": ["40.5", "45.5", "51", "55.5"],
  "11 3/4": ["42", "47", "54", "60"],
  "13 3/8": ["48", "54.5", "61", "68", "72"],
  "16": ["65", "75", "84", "109"],
  "20": ["94", "106.5", "133"],
};

export default function CasingEntryForm({
  casingStrings,
  onAdd,
  onRemove,
  onUpdate,
}: CasingEntryFormProps) {
  const [newCasingType, setNewCasingType] = useState("Surface");
  const [newSize, setNewSize] = useState("9 5/8");
  const [newCustomSize, setNewCustomSize] = useState("");
  const [newWeight, setNewWeight] = useState("");
  const [newHoleSize, setNewHoleSize] = useState("");
  const [newDepthSet, setNewDepthSet] = useState("");
  const [newTopOfCement, setNewTopOfCement] = useState("");
  const [newBottomOfCement, setNewBottomOfCement] = useState("");

  const handleAdd = () => {
    const size = newSize === "Other" ? newCustomSize : newSize;
    if (!size.trim()) {
      alert("Please enter a casing size");
      return;
    }

    const newCasing: UserCasingString = {
      id: `user_casing_${Date.now()}`,
      casing_type: newCasingType,
      size: size.trim(),
      weight: newWeight.trim(),
      hole_size: newHoleSize.trim(),
      depth_set: newDepthSet ? parseFloat(newDepthSet) : null,
      top_of_cement: newTopOfCement ? parseFloat(newTopOfCement) : null,
      bottom_of_cement: newBottomOfCement ? parseFloat(newBottomOfCement) : null,
    };

    onAdd(newCasing);

    // Reset form
    setNewCasingType("Surface");
    setNewWeight("");
    setNewHoleSize("");
    setNewDepthSet("");
    setNewTopOfCement("");
    setNewBottomOfCement("");
    setNewCustomSize("");
  };

  const suggestedWeights = CASING_WEIGHTS[newSize] || [];
  const suggestedHoleSizes = HOLE_SIZES[newSize] || [];

  return (
    <div className="bg-amber-50 dark:bg-amber-900/10 border border-amber-200 dark:border-amber-800 rounded-lg p-4">
      <div className="flex items-center gap-2 mb-3">
        <svg className="w-5 h-5 text-amber-600 dark:text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
        </svg>
        <h4 className="text-sm font-bold text-amber-900 dark:text-amber-100">
          Add Casing Strings
        </h4>
      </div>
      <p className="text-xs text-amber-800 dark:text-amber-200 mb-4">
        Add each casing string installed in the well. Include size, weight, setting depth, and top of cement.
      </p>

      {/* Existing Casing Strings */}
      {casingStrings.length > 0 && (
        <div className="space-y-3 mb-4">
          <h5 className="text-xs font-bold text-amber-900 dark:text-amber-100 uppercase">
            Added Casing Strings
          </h5>
          {casingStrings.map((casing) => (
            <div key={casing.id} className="bg-white dark:bg-gray-800 border-2 border-brand-300 dark:border-brand-700 rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex-1">
                  <p className="text-sm font-semibold text-gray-800 dark:text-white/90">
                    {casing.casing_type} — {casing.size}" Casing {casing.weight ? `(${casing.weight} lb/ft)` : ""} {casing.hole_size ? `in ${casing.hole_size}" hole` : ""}
                  </p>
                  <p className="text-xs text-brand-600 dark:text-brand-400 font-medium mt-1">
                    User Added
                  </p>
                </div>
                <button
                  onClick={() => onRemove(casing.id)}
                  className="ml-3 p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                  title="Remove casing"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </button>
              </div>

              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                    Depth Set (ft)
                  </label>
                  <input
                    type="number"
                    value={casing.depth_set !== null ? casing.depth_set : ""}
                    onChange={(e) => onUpdate(casing.id, "depth_set", e.target.value ? parseFloat(e.target.value) : null)}
                    placeholder="e.g., 7500"
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white dark:bg-gray-800 dark:border-gray-600 dark:text-white focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                    Top of Cement (ft)
                  </label>
                  <input
                    type="number"
                    value={casing.top_of_cement !== null ? casing.top_of_cement : ""}
                    onChange={(e) => onUpdate(casing.id, "top_of_cement", e.target.value ? parseFloat(e.target.value) : null)}
                    placeholder="e.g., 5000"
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white dark:bg-gray-800 dark:border-gray-600 dark:text-white focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                    Bottom of Cement (ft)
                  </label>
                  <input
                    type="number"
                    value={casing.bottom_of_cement !== null ? casing.bottom_of_cement : ""}
                    onChange={(e) => onUpdate(casing.id, "bottom_of_cement", e.target.value ? parseFloat(e.target.value) : null)}
                    placeholder="e.g., 7500"
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white dark:bg-gray-800 dark:border-gray-600 dark:text-white focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add New Casing Form */}
      <div className="pt-4 border-t border-amber-300 dark:border-amber-700">
        <h5 className="text-xs font-bold text-amber-900 dark:text-amber-100 uppercase mb-3">
          Add Casing String
        </h5>
        <div className="bg-white dark:bg-gray-800 border-2 border-dashed border-amber-300 dark:border-amber-700 rounded-lg p-4 space-y-3">
          <div>
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
              Casing Type
            </label>
            <select
              value={newCasingType}
              onChange={(e) => setNewCasingType(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white dark:bg-gray-800 dark:border-gray-600 dark:text-white focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
            >
              <option value="Surface">Surface</option>
              <option value="Intermediate">Intermediate</option>
              <option value="Production">Production</option>
              <option value="Liner">Liner</option>
            </select>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                Casing Size (in)
              </label>
              <select
                value={newSize}
                onChange={(e) => {
                  setNewSize(e.target.value);
                  setNewWeight(""); // Reset weight when size changes
                  setNewHoleSize(""); // Reset hole size when size changes
                }}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white dark:bg-gray-800 dark:border-gray-600 dark:text-white focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
              >
                {CASING_SIZES.map((size) => (
                  <option key={size} value={size}>
                    {size === "Other" ? "Other (custom)" : `${size}"`}
                  </option>
                ))}
              </select>
              {newSize === "Other" && (
                <input
                  type="text"
                  value={newCustomSize}
                  onChange={(e) => setNewCustomSize(e.target.value)}
                  placeholder="Enter custom size"
                  className="w-full mt-2 px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white dark:bg-gray-800 dark:border-gray-600 dark:text-white focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                />
              )}
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                Weight (lb/ft)
              </label>
              {suggestedWeights.length > 0 ? (
                <select
                  value={newWeight}
                  onChange={(e) => setNewWeight(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white dark:bg-gray-800 dark:border-gray-600 dark:text-white focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                >
                  <option value="">Select weight</option>
                  {suggestedWeights.map((w) => (
                    <option key={w} value={w}>
                      {w} lb/ft
                    </option>
                  ))}
                  <option value="other">Other</option>
                </select>
              ) : (
                <input
                  type="text"
                  value={newWeight}
                  onChange={(e) => setNewWeight(e.target.value)}
                  placeholder="e.g., 47"
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white dark:bg-gray-800 dark:border-gray-600 dark:text-white focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                />
              )}
              {newWeight === "other" && suggestedWeights.length > 0 && (
                <input
                  type="text"
                  onChange={(e) => setNewWeight(e.target.value)}
                  placeholder="Enter custom weight"
                  className="w-full mt-2 px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white dark:bg-gray-800 dark:border-gray-600 dark:text-white focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                />
              )}
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                Hole Size (in)
              </label>
              {suggestedHoleSizes.length > 0 ? (
                <select
                  value={newHoleSize}
                  onChange={(e) => setNewHoleSize(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white dark:bg-gray-800 dark:border-gray-600 dark:text-white focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                >
                  <option value="">Select hole size</option>
                  {suggestedHoleSizes.map((h) => (
                    <option key={h} value={h}>{h}"</option>
                  ))}
                  <option value="other">Other</option>
                </select>
              ) : (
                <input
                  type="text"
                  value={newHoleSize}
                  onChange={(e) => setNewHoleSize(e.target.value)}
                  placeholder="e.g., 12 1/4"
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white dark:bg-gray-800 dark:border-gray-600 dark:text-white focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                />
              )}
              {newHoleSize === "other" && suggestedHoleSizes.length > 0 && (
                <input
                  type="text"
                  onChange={(e) => setNewHoleSize(e.target.value)}
                  placeholder="Enter custom hole size"
                  className="w-full mt-2 px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white dark:bg-gray-800 dark:border-gray-600 dark:text-white focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                />
              )}
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                Depth Set (ft MD)
              </label>
              <input
                type="number"
                value={newDepthSet}
                onChange={(e) => setNewDepthSet(e.target.value)}
                placeholder="e.g., 7500"
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white dark:bg-gray-800 dark:border-gray-600 dark:text-white focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                Top of Cement (ft MD)
              </label>
              <input
                type="number"
                value={newTopOfCement}
                onChange={(e) => setNewTopOfCement(e.target.value)}
                placeholder="e.g., 5000"
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white dark:bg-gray-800 dark:border-gray-600 dark:text-white focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                Bottom of Cement (ft MD)
              </label>
              <input
                type="number"
                value={newBottomOfCement}
                onChange={(e) => setNewBottomOfCement(e.target.value)}
                placeholder="e.g., 7500"
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white dark:bg-gray-800 dark:border-gray-600 dark:text-white focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
              />
            </div>
          </div>
          <button
            onClick={handleAdd}
            className="w-full px-4 py-2 bg-brand-500 text-white text-sm font-medium rounded-lg hover:bg-brand-600 transition-colors flex items-center justify-center gap-2"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Add Casing String
          </button>
        </div>
      </div>

      {/* Empty State */}
      {casingStrings.length === 0 && (
        <div className="text-center py-4 text-amber-700 dark:text-amber-300">
          <p className="text-sm">No casing strings added yet.</p>
          <p className="text-xs mt-1">Add casing strings above to generate accurate plugging plans.</p>
        </div>
      )}
    </div>
  );
}
